// All GraphQL documents in one place. Field-value selections need the
// issue_fields preview — pass preview: 'issue_fields' through the proxy.

// 2000 open issues — beyond this something is wrong.
export const MAX_UNIVERSE_PAGES = 20

export const ISSUE_CORE_FRAGMENT = `
  fragment IssueCore on Issue {
    id
    number
    title
    url
    state
    createdAt
    updatedAt
    author { login avatarUrl }
    issueType { name }
    labels(first: 20) { nodes { name } }
    assignees(first: 10) { nodes { login, name, avatarUrl } }
    comments { totalCount }
    subIssues(first: 1) { totalCount }
    issueFieldValues(first: 20) {
      nodes {
        __typename
        ... on IssueFieldSingleSelectValue { name optionId field { ... on IssueFieldSingleSelect { name } } }
        ... on IssueFieldDateValue { value field { ... on IssueFieldDate { name } } }
      }
    }
    linkedPrs: closedByPullRequestsReferences(first: 5, includeClosedPrs: true) {
      nodes { number title state isDraft url repository { nameWithOwner } }
    }
  }
`

/** The 50 most recently closed — closed issues live outside the open-only
 * universe, so "what just finished" needs its own query. */
export const CLOSED_ISSUES = `
  ${ISSUE_CORE_FRAGMENT}
  query ClosedIssues($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      issues(first: 50, states: CLOSED, orderBy: { field: UPDATED_AT, direction: DESC }) {
        nodes { ...IssueCore closedAt stateReason }
      }
    }
  }
`

export const ORG_ISSUE_FIELDS = `
  query OrgIssueFields($org: String!) {
    organization(login: $org) {
      issueFields(first: 20) {
        nodes {
          __typename
          ... on IssueFieldSingleSelect { id name options { id name color } }
          ... on IssueFieldNumber { id name }
          ... on IssueFieldDate { id name }
          ... on IssueFieldText { id name }
        }
      }
    }
  }
`

/** Sheet detail — body, sub-issues, linked PRs, comments, stage timeline. */
export const ISSUE_DETAIL = `
  ${ISSUE_CORE_FRAGMENT}
  query IssueDetail($owner: String!, $name: String!, $number: Int!) {
    repository(owner: $owner, name: $name) {
      issue(number: $number) {
        ...IssueCore
        body
        bodyHTML
        subIssuesList: subIssues(first: 50) {
          nodes { id number title state repository { nameWithOwner } }
        }
        closedByPullRequestsReferences(first: 20, includeClosedPrs: true) {
          nodes { id number title state repository { nameWithOwner } url }
        }
        timelineItems(first: 50, itemTypes: [CROSS_REFERENCED_EVENT]) {
          nodes {
            __typename
            ... on CrossReferencedEvent {
              source {
                ... on PullRequest { id number title state url repository { nameWithOwner } }
              }
            }
          }
        }
        commentsPage: comments(last: 30) {
          nodes {
            id
            author { login avatarUrl }
            createdAt
            body
            bodyHTML
          }
        }
      }
    }
  }
`

export const UPDATE_ISSUE_FIELD = `
  mutation SetIssueField($issueId: ID!, $field: IssueFieldCreateOrUpdateInput!) {
    updateIssueFieldValue(input: { issueId: $issueId, issueField: $field }) {
      issue { id }
    }
  }
`

// Clearing a value is its OWN mutation — updateIssueFieldValue rejects an empty
// value ("must provide a valid value to match the selected type").
export const DELETE_ISSUE_FIELD_VALUE = `
  mutation ClearIssueField($issueId: ID!, $fieldId: ID!) {
    deleteIssueFieldValue(input: { issueId: $issueId, fieldId: $fieldId }) {
      issue { id }
    }
  }
`

export const UPDATE_ISSUE = `
  mutation UpdateIssue($id: ID!, $title: String, $body: String) {
    updateIssue(input: { id: $id, title: $title, body: $body }) {
      issue { id }
    }
  }
`

// Native Issue Type lives on the issue itself, not in IssueFieldValues — its own
// updateIssue mutation. A null issueTypeId clears the type. Kept separate from
// UPDATE_ISSUE so a title/body edit never touches the type.
export const SET_ISSUE_TYPE = `
  mutation SetIssueType($id: ID!, $issueTypeId: ID) {
    updateIssue(input: { id: $id, issueTypeId: $issueTypeId }) {
      issue { id issueType { name } }
    }
  }
`

export const CREATE_ISSUE = `
  mutation CreateIssue($repositoryId: ID!, $title: String!, $body: String!, $issueTypeId: ID, $labelIds: [ID!]) {
    createIssue(input: { repositoryId: $repositoryId, title: $title, body: $body, issueTypeId: $issueTypeId, labelIds: $labelIds }) {
      issue { id number url }
    }
  }
`

export const REPO_META = `
  query RepoMeta($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      id
      issueTypes(first: 10) { nodes { id name } }
      labels(first: 100) { nodes { id name } }
      assignableUsers(first: 50) { nodes { id login name avatarUrl } }
    }
  }
`

export const ADD_LABELS = `
  mutation AddLabels($labelableId: ID!, $labelIds: [ID!]!) {
    addLabelsToLabelable(input: { labelableId: $labelableId, labelIds: $labelIds }) {
      clientMutationId
    }
  }
`

export const REMOVE_LABELS = `
  mutation RemoveLabels($labelableId: ID!, $labelIds: [ID!]!) {
    removeLabelsFromLabelable(input: { labelableId: $labelableId, labelIds: $labelIds }) {
      clientMutationId
    }
  }
`

export const ADD_ASSIGNEES = `
  mutation AddAssignees($assignableId: ID!, $assigneeIds: [ID!]!) {
    addAssigneesToAssignable(input: { assignableId: $assignableId, assigneeIds: $assigneeIds }) {
      clientMutationId
    }
  }
`

export const REMOVE_ASSIGNEES = `
  mutation RemoveAssignees($assignableId: ID!, $assigneeIds: [ID!]!) {
    removeAssigneesFromAssignable(input: { assignableId: $assignableId, assigneeIds: $assigneeIds }) {
      clientMutationId
    }
  }
`

export const BODY_SEARCH = `
  query BodySearch($query: String!) {
    search(query: $query, type: ISSUE, first: 20) {
      issueCount
      nodes {
        ... on Issue { id number title state url }
      }
    }
  }
`

export const ADD_COMMENT = `
  mutation AddComment($subjectId: ID!, $body: String!) {
    addComment(input: { subjectId: $subjectId, body: $body }) {
      commentEdge { node { id } }
    }
  }
`

export const CLOSE_ISSUE = `
  mutation CloseIssue($id: ID!, $reason: IssueClosedStateReason) {
    closeIssue(input: { issueId: $id, stateReason: $reason }) {
      issue { id }
    }
  }
`

export const REOPEN_ISSUE = `
  mutation ReopenIssue($id: ID!) {
    reopenIssue(input: { issueId: $id }) {
      issue { id }
    }
  }
`

export const DELETE_ISSUE = `
  mutation DeleteIssue($id: ID!) {
    deleteIssue(input: { issueId: $id }) {
      clientMutationId
    }
  }
`

export const CONTEXT_ORDER = `
  query ContextOrder($owner: String!, $name: String!, $expr: String!) {
    repository(owner: $owner, name: $name) {
      object(expression: $expr) { ... on Blob { text } }
    }
  }
`

// One nested read of the whole orders/ tree (backlog.json at the root,
// mine/<login>.json one level down, sprint/<slug>/<groupBy>.json two levels
// down) — cheaper than a request per file. `path` rides on every entry so the
// depth doesn't matter to the walker.
export const ALL_ORDERS = `
  query AllOrders($owner: String!, $name: String!, $expr: String!) {
    repository(owner: $owner, name: $name) {
      object(expression: $expr) {
        ... on Tree { entries {
          path type object {
            ... on Blob { text }
            ... on Tree { entries {
              path type object {
                ... on Blob { text }
                ... on Tree { entries {
                  path type object { ... on Blob { text } }
                } }
              }
            } }
          }
        } }
      }
    }
  }
`

export const SAVED_SEARCHES = `
  query SavedSearches($owner: String!, $name: String!, $orgExpr: String!, $mineExpr: String!) {
    repository(owner: $owner, name: $name) {
      org: object(expression: $orgExpr) {
        ... on Tree { entries { name type object { ... on Blob { text } } } }
      }
      mine: object(expression: $mineExpr) {
        ... on Tree { entries { name type object { ... on Blob { text } } } }
      }
    }
  }
`

export const TEMPLATE_TREE = `
  query TemplateTree($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      object(expression: "HEAD:.github/ISSUE_TEMPLATE") {
        ... on Tree { entries { name type } }
      }
    }
  }
`

export const DOC_TREES = `
  query DocTrees($owner: String!, $name: String!) {
    repository(owner: $owner, name: $name) {
      root: object(expression: "HEAD:") {
        ... on Tree { entries { name type } }
      }
      guides: object(expression: "HEAD:guides") {
        ... on Tree { entries { name type } }
      }
    }
  }
`
